import { useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Utils
import { get } from '@/utils/client';

// API Endpoints
import { COMMENT_REPLIES } from '@/lib/routes/apiEndpoints';

const getKey = (pageIndex, previousPageData, activeSPID, mediaId, commentId, firstLimit, limit) => {
    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) {
        return `${injectEndpointUrlParams(COMMENT_REPLIES, {
            SPID: activeSPID,
            mediaID: mediaId,
            commentID: commentId
        })}?per_page=${firstLimit}`;
    }

    // Add the cursor to the API endpoint
    return `${injectEndpointUrlParams(COMMENT_REPLIES, {
        SPID: activeSPID,
        mediaID: mediaId,
        commentID: commentId
    })}?per_page=${limit}&cursor=${previousPageData.cursor}`;
};

const useReplies = ({
    activeSPID,
    mediaId,
    commentId,
    firstLimit = 2,
    limit = 10,
    options = {}
} = {}) => {
    const {
        data: dataReplies,
        size,
        setSize,
        mutate: mutateReplies,
        error: repliesError
    } = useSWRInfinite(
        (pageIndex, previousPageData) =>
            getKey(pageIndex, previousPageData, activeSPID, mediaId, commentId, firstLimit, limit),
        (url) => get(url).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    const replies = useMemo(() => {
        let arrayReplies = [];
        if (dataReplies) {
            dataReplies.map((page) => {
                arrayReplies = [...arrayReplies, ...page.data];
            });
        }
        return arrayReplies;
    }, [dataReplies]);

    const isLoadingInitialData = !dataReplies && !repliesError;
    const isLoadingMore = size > 0 && dataReplies && typeof dataReplies[size - 1] === 'undefined';

    return {
        replies,
        countReplies: replies.length,
        loadMoreReplies: () => setSize(size + 1), // Function to load more replies (load more button)
        mutateReplies,
        repliesError,
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore: dataReplies && typeof dataReplies[dataReplies.length - 1].cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading,
    };
};

export default useReplies;
